import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Pill, Btn, Table, Field, Input, Select, Avatar, Empty } from "./ui";
import { toast } from "sonner";
import { Plus, Check, X, Plane } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

export default function Leaves() {
  const { locationId } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(null);
  const [reject, setReject] = useState(null);
  const [balances, setBalances] = useState(null);

  const load = () => api.get("/hr/leaves", { params: { status: status || undefined, location_id: locationId || undefined } })
    .then(setRows).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    api.get("/hr/leave-types").then(setTypes);
    api.get("/hr/employees", { params: { location_id: locationId || undefined } }).then(setEmployees);
    /* eslint-disable-next-line */
  }, [locationId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, locationId]);

  const save = async () => {
    try {
      await api.post("/hr/leaves", form);
      toast.success("Leave request submitted"); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const approve = async (r) => {
    try { await api.post(`/hr/leaves/${r.id}/approve`, {}); toast.success("Leave approved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doReject = async () => {
    if (!reject.reason?.trim()) return toast.error("A rejection reason is required");
    try {
      await api.post(`/hr/leaves/${reject.id}/reject`, { reason: reject.reason });
      toast.success("Leave rejected"); setReject(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const showBalances = async (eid, name) => {
    try { setBalances({ name, rows: await api.get(`/hr/leaves/balances/${eid}`) }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const pending = rows.filter((r) => r.status === "pending");

  return (
    <div className="space-y-4" data-testid="hr-leaves-page">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
        {[["Pending", pending.length, HC.amber, HC.amberTint], ["Approved", rows.filter((r) => r.status === "approved").length, HC.green, HC.greenTint],
          ["Rejected", rows.filter((r) => r.status === "rejected").length, HC.red, HC.redTint]].map(([l, n, c, bg]) => (
          <Card key={l} className="!p-4" testid={`leave-kpi-${l.toLowerCase()}`}>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: bg, color: c }}><Plane size={18} /></span>
              <div>
                <div className="text-[12px] font-semibold" style={{ color: HC.muted }}>{l} requests</div>
                <div className="font-brand font-bold text-2xl" style={{ color: HC.text }}>{n}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <Field label="Status" className="lg:w-56">
            <Select data-testid="leave-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All requests</option>
              {["pending", "approved", "rejected", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <div className="flex-1" />
          <Btn onClick={() => setForm({ employee_id: "", leave_type_id: types[0]?.id || "", date_from: "", date_to: "", reason: "" })}
            data-testid="hr-new-leave"><Plus size={15} /> New Leave Request</Btn>
        </div>
      </Card>

      <Card>
        <SectionHead title={`Leave requests (${rows.length})`} sub="Approvals and rejections are audited; rejection requires a reason" />
        <Table
          testid="hr-leaves-table" rows={rows} empty="No leave requests"
          columns={[
            { key: "employee_name", label: "EMPLOYEE", render: (r) => (
                <button onClick={() => showBalances(r.employee_id, r.employee_name)} className="flex items-center gap-2.5 text-left"
                  data-testid={`leave-emp-${r.id}`}>
                  <Avatar name={r.employee_name} />
                  <span><span className="block font-semibold">{r.employee_name}</span>
                    <span className="block text-[11px]" style={{ color: HC.soft }}>{r.employee_no}</span></span>
                </button>) },
            { key: "leave_type", label: "TYPE" },
            { key: "date_from", label: "FROM" },
            { key: "date_to", label: "TO" },
            { key: "days", label: "DAYS", align: "right" },
            { key: "reason", label: "REASON", render: (r) => <span className="text-[12px]" style={{ color: HC.muted }}>{r.reason || "—"}</span> },
            { key: "status", label: "STATUS", render: (r) => <Pill status={r.status} /> },
            { key: "actions", label: "", align: "right", render: (r) => r.status === "pending" ? (
                <span className="flex items-center gap-1.5 justify-end">
                  <Btn variant="outline" className="h-8 !px-2.5" onClick={() => approve(r)} data-testid={`leave-approve-${r.id}`}><Check size={14} /></Btn>
                  <Btn variant="danger" className="h-8 !px-2.5" onClick={() => setReject({ ...r, reason: "" })} data-testid={`leave-reject-${r.id}`}><X size={14} /></Btn>
                </span>) : <span className="text-[11.5px]" style={{ color: HC.soft }}>{r.approver_name || "—"}</span> },
          ]}
        />
      </Card>

      <Card>
        <SectionHead title="Leave types" sub="Configurable entitlements" />
        <Table
          testid="hr-leave-types" rows={types} empty="No leave types"
          columns={[
            { key: "name", label: "TYPE", render: (r) => <span className="font-semibold">{r.name}</span> },
            { key: "code", label: "CODE" },
            { key: "days_per_year", label: "DAYS / YEAR", align: "right" },
            { key: "paid", label: "PAID", render: (r) => <Pill status={r.paid ? "active" : "draft"} text={r.paid ? "Paid" : "Unpaid"} /> },
          ]}
        />
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New leave request</DialogTitle>
            <DialogDescription>Approved leave automatically marks attendance as On Leave.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <Field label="Employee">
                <Select data-testid="leave-employee" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
                  <option value="">Select employee</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </Select>
              </Field>
              <Field label="Leave type">
                <Select data-testid="leave-type" value={form.leave_type_id} onChange={(e) => setForm({ ...form, leave_type_id: e.target.value })}>
                  {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="From"><Input data-testid="leave-from" type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} /></Field>
                <Field label="To"><Input data-testid="leave-to" type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} /></Field>
              </div>
              <Field label="Reason"><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Btn variant="outline" onClick={() => setForm(null)}>Cancel</Btn>
            <Btn onClick={save} data-testid="leave-save">Submit request</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject leave request</DialogTitle>
            <DialogDescription>{reject?.employee_name} · {reject?.date_from} → {reject?.date_to}</DialogDescription>
          </DialogHeader>
          <Field label="Reason (required)">
            <Input data-testid="leave-reject-reason" value={reject?.reason || ""} onChange={(e) => setReject({ ...reject, reason: e.target.value })} />
          </Field>
          <DialogFooter>
            <Btn variant="outline" onClick={() => setReject(null)}>Cancel</Btn>
            <Btn onClick={doReject} data-testid="leave-confirm-reject">Reject</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!balances} onOpenChange={(o) => !o && setBalances(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave balances — {balances?.name}</DialogTitle>
            <DialogDescription>Entitlement vs approved leave taken this year.</DialogDescription>
          </DialogHeader>
          {balances?.rows?.length ? (
            <Table testid="hr-leave-balances" rows={balances.rows} rowKey={(r) => r.leave_type_id}
              columns={[
                { key: "name", label: "TYPE" },
                { key: "entitlement", label: "ENTITLEMENT", align: "right" },
                { key: "taken", label: "TAKEN", align: "right" },
                { key: "balance", label: "BALANCE", align: "right", render: (r) => (
                    <span className="font-semibold" style={{ color: r.balance <= 0 ? HC.red : HC.green }}>{r.balance}</span>) },
              ]} />
          ) : <Empty />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
